// Utility for preserving intended admin navigation target across auth redirects
import { logger } from '@/utils/logger';
const STORAGE_KEY = 'admin_next_path_v1';

// Core allow-list of stable admin pages (expand as needed)
const STATIC_ALLOWED = new Set<string>([
  '/admin',
  '/admin/projects',
  '/admin/sectors',
  '/admin/companies',
  '/admin/services',
  '/admin/users',
  '/admin/media',
  '/admin/settings',
  '/admin/analytics',
  '/admin/navigation',
  '/admin/pages',
]);

// Dynamic route patterns (e.g., editors, detail pages)
const dynamicPatterns: RegExp[] = [
  /^\/admin\/dynamic-editor\/[a-zA-Z0-9-]+$/, // dynamic editor by slug
  /^\/admin\/pages\/[a-zA-Z0-9-]+$/,
  /^\/admin\/pages\/[a-zA-Z0-9-]+\/edit$/, // unified editor
  /^\/admin\/pages\/[a-zA-Z0-9-]+\/inspect$/,
];

// Explicitly blocked prefixes (never redirect back to these)
const BLOCKED_PREFIXES = ['/admin/login', '/admin/auth', '/admin/redirect'];

export function normalizeAdminPath(raw: string): string | null {
  if (!raw) return null;
  try {
    if (raw.startsWith('http')) {
      const u = new URL(raw);
      raw = u.pathname + (u.search || '');
    }
  } catch {
    return null;
  }
  return raw.startsWith('/') ? raw : '/' + raw;
}

export function isSafeAdminPath(path: string | null | undefined): boolean {
  if (!path) return false;
  const normalized = normalizeAdminPath(path);
  if (!normalized) return false;
  if (!normalized.startsWith('/admin')) return false;
  const pathname = normalized.split('?')[0];
  if (BLOCKED_PREFIXES.some(p => pathname.startsWith(p))) return false;
  if (STATIC_ALLOWED.has(pathname)) return true;
  if (dynamicPatterns.some(r => r.test(pathname))) return true;
  // Structured debug (dev / runtime debug flag)
  logger.debug('[adminRedirect] rejected unsafe path', { ctx: { pathname } });
  return false;
}

export function setPendingAdminPath(path: string) {
  if (!isSafeAdminPath(path)) return;
  try { sessionStorage.setItem(STORAGE_KEY, path); } catch { /* ignore */ }
}

export function getPendingAdminPath(): string | null {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    return v && isSafeAdminPath(v) ? v : null;
  } catch { return null; }
}

export function consumePendingAdminPath(): string | null {
  const v = getPendingAdminPath();
  if (v) {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }
  return v;
}

// Optional debug helper
export function debugPendingAdminPath() {
  logger.info('[adminRedirect] pending path snapshot', { ctx: { value: getPendingAdminPath() } });
}
